import { defineComponent as _defineComponent } from 'vue'
import { computed, ref } from 'vue';

import { ObjectType, Offer } from '@/@types/graphql-types';
import type { TitleDetail } from '@/interfaces/title-details-graphql';
import { ImpressionTrackingEvents } from '@/enums/events';
import { useI18n } from '@/helpers/composables/useI18n';
import type { UsePromotion } from '@/helpers/composables/usePromotion';
import { usePromotion } from '@/helpers/composables/usePromotion';
import { ClickoutUrl } from '@/helpers/clickout-helper';
import { useLanguageStore, useExperimentStore, useUserStore } from '@/helpers/composables/useStores';
import { OfferLocation } from '@/helpers/offer-helper';
import { useArbitrage } from '@/helpers/composables/useArbitrage';
import { FALLBACK_PROMOTION_OFFER, CatalogueItem, getRawCatalogue } from '@/helpers/promotion-helper';
import { getOfferImpressionContext, observableOptions } from '@/helpers/composables/useOfferImpressionTracking';
import { TrackingHelper } from '@/helpers/tracking/tracking-helper';
import { useQuery } from '@/helpers/composables/useApollo';
import {
	GetPackageProviderPlansDocument,
	GetPackageProviderPlansQuery,
} from '@/components/buybox-new/graphql/GetPackageProviderPlans.query';
import { TitleOfferFragment } from '@/components/buybox/graphql/fragments/Offer.fragment';
import { subscriptionPricesByCountry } from '@/components/buybox-new/new-buybox-config';

import AmazonOSFreeTrialExp2, {
	amazonPlanOffer,
	applePlanOffer,
	type AmazonOSFreeTrialExp2Variants,
} from '@/components/experiments/AmazonOSFreeTrialExp2';
import { useUserAgent } from '@/helpers/composables/useUserAgent';
import { useMounted } from '@vueuse/core';
import { SnowplowClickoutContextGraphql } from '@/helpers/tracking/providers';


export default /*#__PURE__*/_defineComponent({
  __name: 'AmazonOSFreeTrialExpPromotionRowNew',
  props: {
    offers: { default: () => [] },
    title: null,
    isEpisodeBuybox: { type: Boolean },
    variant: null
  },
  emits: ["clickOut", "impressionCallback"],
  setup(__props: any, { emit }: { emit: ({
	(
		e: 'clickOut',
		payload: {
			buyboxOffer: UsePromotion['promotionOffer'];
			offerLocation: OfferLocation.FREE_TRIAL;
			row: HTMLElement | null;
		}
	): void;
	(e: 'impressionCallback'): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as { offers: Offer[], title: TitleDetail, isEpisodeBuybox?: boolean, variant?: AmazonOSFreeTrialExp2Variants | null };

const container = ref<HTMLElement | null>(null);
const { t, tc } = useI18n();

const { country, language } = useLanguageStore();





const { trackTagEvent, withArbitrageTracking, isArbitrating } = useArbitrage();

const { inPromotionCatalogue, promotionCatalogue } = usePromotion({
	title: () => props.title,
	offers: () => props.offers,
});

const { deviceOs } = useUserAgent();

const appleOS = computed(() => ['ios', 'macos'].includes(deviceOs.value));

const experimentPromotionCatalogue = computed(() => {
	const { isLoggedIn } = useUserStore();
	const hasAmazonOffer =
		props.offers.find(offer => offer.package.packageId === 9 || offer.package.packageId === 119) != null;
	const title = props.title;
	const rawCatalogue = getRawCatalogue({ country, title });
	// VARIANT_1
	if (props.variant === 'variant_1') {
		// If the title detail page has an Amazon Prime offer and the device is iOS and the user is not logged in, we need to show the Apple Free Trial offer
		if (hasAmazonOffer && appleOS.value && !isLoggedIn.value) {
			const foundItem = rawCatalogue.find(item => item.packageId === 350);
			if (foundItem) {
				const catalogue = new Map<number, CatalogueItem>();
				catalogue.set(foundItem.packageId, foundItem);
				return catalogue;
			}
		}
	}
	// CONTROL, FALLBACK & REGULAR BEHAVIOUR
	return promotionCatalogue.value;
});

const bestTitleOffer = computed(() => {
	const availableOffers = props.offers;

	return (
		Array.from(experimentPromotionCatalogue.value.keys())
			.map(id => availableOffers.find(offer => offer.package.packageId === id))
			.find(offer => offer != null) ?? null
	);
});

const catalogueItem = computed(() => {
	if (bestTitleOffer.value) {
		return experimentPromotionCatalogue.value.get(bestTitleOffer.value.package.packageId) ?? null;
	}
	const { done, value } = experimentPromotionCatalogue.value.values().next();
	if (!done) return value;
	return null;
});

/** The top offer from the promotion catalogue. */
const promotionOffer = computed(() => {
	if (catalogueItem.value) {
		return catalogueItem.value.offer(bestTitleOffer, country, inPromotionCatalogue);
	}
	return bestTitleOffer.value;
});

const { result: packageProviderPlans } = useQuery<GetPackageProviderPlansQuery>(
	GetPackageProviderPlansDocument,
	() => ({
		country: country.value,
		language: language.value,
		packageIds: promotionOffer.value?.package.packageId ? [promotionOffer.value?.package.packageId] : [],
	}),
	{
		enabled: !inPromotionCatalogue.value,
		errorPolicy: 'all',
	}
);

const packagePlan = computed(() => {
	let mainPackage = inPromotionCatalogue.value
		? promotionOffer.value?.package
		: packageProviderPlans.value?.packages?.[0];

	let planOffer = null;
	switch (catalogueItem.value?.packageId) {
		case 9:
			planOffer = amazonPlanOffer;
			break;
		case 350:
			planOffer = applePlanOffer;
			break;
	}

	mainPackage = {
		...mainPackage,
		id: mainPackage?.id ?? '',
		clearName: mainPackage?.clearName ?? '',
		planOffers: [{ ...planOffer, __typename: 'PackagePlanOffer', children: planOffer?.children ?? [] }],
	};

	if (!mainPackage) return null;

	return getPlanOffer(mainPackage);
});

const subText = computed(() => {
	if (!catalogueItem.value || !promotionOffer?.value) return '';

	const provider = promotionOffer?.value?.package.clearName;

	if (
		(promotionOffer.value.package.packageId === 9 && props.offers.find(offer => offer.package.packageId === 9)) ||
		(promotionOffer.value.package.packageId === 350 && props.offers.find(offer => offer.package.packageId === 350))
	) {
		return t('WEBAPP_FREE_TRIAL_TEXT', { provider });
	}

	return props.title.objectType === ObjectType.Movie
		? t('WEBAPP_WATCH_SIMILAR_MOVIES_FREE_ON_X', { provider })
		: t('WEBAPP_WATCH_SIMILAR_TV_SHOWS_FREE_ON_X', { provider });
});

const subscriptionPrice = computed(() =>
	subscriptionPricesByCountry[country.value]?.find(provider => {
		return provider.id === promotionOffer?.value?.package?.packageId;
	})
);

const freeTrialText = computed(() => {
	if (packagePlan.value?.durationDays) {
		return packagePlan.value.durationDays === 1
			? t('WEBAPP_1_DAY_FREE')
			: t('WEBAPP_X_DAYS_FREE', { days: packagePlan.value.durationDays });
	}
	// Fallback
	let days = null;
	if (subscriptionPrice.value && subscriptionPrice.value.free_trial) {
		// jujutsu to get the number of days from the string if it's from the config file
		const firstSpace = subscriptionPrice.value.free_trial.indexOf(' ');
		days = Number(subscriptionPrice.value.free_trial.slice(0, firstSpace));
	} else {
		switch (promotionOffer?.value?.package.packageId) {
			case 350: // Apple TV+
			case 283: // Crunchyroll
				days = 7;
				break;
			case 9: // Amazon
			case 119: // Amazon
				days = 3;
				break;
		}
	}

	if (days == null || Number.isNaN(days)) return null;
	return days === 1 ? t('WEBAPP_1_DAY_FREE') : t('WEBAPP_X_DAYS_FREE', { days });
});

const hasAppleTvPlusOffer = computed(() => props.offers?.some(offer => offer.package.packageId === 350));

const isMounted = useMounted();

const clickoutLink = computed(() => {
	if (!promotionOffer.value) return '';

	const packageId = promotionOffer.value?.package.packageId ?? 350;

	const clickoutUrl = new ClickoutUrl(promotionOffer.value, {}, false, true).set('uct_country', country.value);

	if (props.variant && isMounted.value) {
		const experimentOffer = props.offers.find(
			offer => offer.package.packageId === promotionOffer.value?.package.packageId
		);
		const experimentClickoutUrl = new ClickoutUrl(experimentOffer, {}, false, true).set(
			'uct_country',
			country.value
		);
		return promotionOffer.value.package.packageId === 9 ? experimentClickoutUrl.toString() : clickoutUrl.toString();
	}

	if (isArbitrating.value) {
		const arbitrageUrl = withArbitrageTracking(promotionOffer);
		if (arbitrageUrl) return arbitrageUrl.toString();
	}

	if (promotionOffer.value?.package.packageId !== 350 && promotionOffer.value?.id === FALLBACK_PROMOTION_OFFER) {
		return clickoutUrl.toString();
	}

	const conversionTrackingByPackageId: Record<number, Record<'param' | 'value', string | null>> = {
		350: { value: hasAppleTvPlusOffer.value ? 'atds' : 'natds', param: 'ct' },
	};

	const partnerTokenByPackageId: Record<number, Record<'param' | 'value', string>> = {
		350: { value: hasAppleTvPlusOffer.value ? 'justwatch_tv_5' : 'justwatch_tv_6', param: 'itsct' },
	};

	if (partnerTokenByPackageId[packageId]?.value) {
		clickoutUrl.setOnOffer(partnerTokenByPackageId[packageId].param, partnerTokenByPackageId[packageId].value);
	}

	if (!!conversionTrackingByPackageId[packageId] && conversionTrackingByPackageId[packageId].param) {
		clickoutUrl.setOnOffer(
			conversionTrackingByPackageId[packageId].param,
			conversionTrackingByPackageId[packageId].value
		);
	}

	return clickoutUrl.toString();
});

const packageImg = computed(() => {
	return promotionOffer.value?.package.iconWide
		? JW_CONFIG.IMAGESCALER_URL + promotionOffer.value.package.iconWide
		: null;
});

const currency = computed(() => {
	if (country.value === 'GB') return 'GBP';
	else if (country.value === 'IN') return 'INR';
	else if (['DE', 'FR', 'ES', 'IT'].includes(country.value)) return 'EUR';
	else if (country.value === 'CA') return 'CAD';
	else if (country.value === 'AU') return 'AUD';
	else if (country.value === 'MX') return 'MXN';
	else if (country.value === 'BR') return 'BRL';
	else if (country.value === 'CO') return 'COP';
	else if (country.value === 'AR') return 'ARS';
	else if (country.value === 'CL') return 'CLF';
	else return 'USD';
});

const formatCurrency = (price: number) => {
	const locale = language.value.includes('-') ? language.value : `${language.value}-${country.value}`;

	return new Intl.NumberFormat(locale, {
		style: 'currency',
		currency: currency.value,
	}).format(price);
};

const retailPrice = computed(() => {
	if (packagePlan.value?.retailPrice) return t('WEBAPP_THEN_X_PER_MONTH', { price: packagePlan.value.retailPrice });

	// Fallback
	if (!subscriptionPrice.value || !subscriptionPrice.value.price) return null;
	if (['CO', 'CL', 'PE'].includes(country.value))
		return t('WEBAPP_THEN_X_PER_MONTH', { price: subscriptionPrice.value.price_formatted });
	return t('WEBAPP_THEN_X_PER_MONTH', { price: formatCurrency(Number(subscriptionPrice.value.price)) });
});

const onVisibilityChanged = (isVisible: boolean) => {
	if (!isVisible || !promotionOffer?.value) return;
	TrackingHelper.trackEvent(
		ImpressionTrackingEvents.OFFER,
		{
			action: 'impression',
			nonInteraction: true,
			value: OfferLocation.FREE_TRIAL,
			label: promotionOffer?.value.id === FALLBACK_PROMOTION_OFFER ? 'provider' : undefined,
		},
		[getOfferImpressionContext(promotionOffer?.value)]
	);
};

const observeVisibilityOptions = computed(() => ({
	...observableOptions,
	callback: (isVisible: boolean) => onVisibilityChanged(isVisible),
}));

const onClickOut = () => {
	trackTagEvent(promotionOffer);

	const clickoutContext = SnowplowClickoutContextGraphql.fromProviderOffer(promotionOffer.value as Offer);
	AmazonOSFreeTrialExp2.trackEvent({
		action: 'click',
		label: 'free_trial_clickout_buybox',
		contexts: [clickoutContext],
	});

	emit('clickOut', {
		buyboxOffer: promotionOffer?.value,
		offerLocation: OfferLocation.FREE_TRIAL,
		row: container.value,
	});
};

function getProviderIcon(iconUrl: string | null, format: string) {
	return iconUrl ? iconUrl.replace('{format}', format) : undefined;
}

function getPlanOffer(pckg: TitleOfferFragment['package'] | GetPackageProviderPlansQuery['packages'][0]) {
	console.log('pckg', pckg);
	if (pckg.planOffers.length > 0) {
		const plan = pckg.planOffers.find(offer => offer.isTrial || offer.children.length > 0);
		if (plan && plan.children.length > 0) {
			return { ...plan.children[0], retailPrice: plan.retailPrice };
		}
		return plan;
	}
	return null;
}

return { __sfc: true,container, t, tc, country, language, props, emit, trackTagEvent, withArbitrageTracking, isArbitrating, inPromotionCatalogue, promotionCatalogue, deviceOs, appleOS, experimentPromotionCatalogue, bestTitleOffer, catalogueItem, promotionOffer, packageProviderPlans, packagePlan, subText, subscriptionPrice, freeTrialText, hasAppleTvPlusOffer, isMounted, clickoutLink, packageImg, currency, formatCurrency, retailPrice, onVisibilityChanged, observeVisibilityOptions, onClickOut, getProviderIcon, getPlanOffer }
}

})